import { keepPreviousData } from "@tanstack/react-query";

import { Optional, WithStringValues } from "common/utils/types";
import { useFetchQuery } from "common/queries/hooks";

import { useBreakpoints } from "client/common/hooks";

import { clearedFilterValues, hasAnySelectedFilterValue, filtersWithOptions } from "../utils";
import {
  BrowseSearch,
  BrowseAllPatientKind,
  IProductLineDetails,
  IProductQueryResponse,
  ProductLineSearch,
  ProductLineListSearch,
  ProductDealsSearch,
  ProductDeals,
  ProductSearchSearch,
  ProductSearch,
  IClientBrowse,
  CarouselDisplay,
  IncentiveDetails,
  IncentiveItemsSearch,
  RebateDetails,
  RebateItemsSearch,
  ClientCategory,
  PromotionItems,
  ProductLineAvailabilitySearch,
  ProductLineAvailabilityResponse,
  ItemPricingSearch,
  ItemPricingResponse,
  FilterDefinition,
  FilterValue,
} from "../types";
import { keys, urls } from "./constants";

export const useProductLineQuery = (id: number) => {
  return useFetchQuery<IProductLineDetails>(keys.productLineDetail(id), {
    fetch: { url: urls.productLine(id) },
    config: { enabled: !!id },
  });
};

export const useProductQuery = (id: number, { enabled = true }: { enabled?: boolean } = {}) => {
  return useFetchQuery<IProductQueryResponse>(keys.productDetail(id), {
    fetch: { url: urls.product(id) },
    config: { enabled: enabled && !!id },
  });
};

export const useProductLineSearchQuery = (
  search: ProductLineListSearch,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<ProductLineSearch, WithStringValues<ProductLineListSearch>>(
    keys.productLineSearch(search),
    {
      fetch: { url: urls.productLineSearch(), body: search },
      config: {
        enabled,
        placeholderData: keepPreviousData,
      },
    },
  );
};

export const useProductSearchQuery = (
  search: WithStringValues<ProductSearchSearch>,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<ProductSearch, WithStringValues<ProductSearchSearch>>(
    keys.productSearch(search),
    {
      fetch: {
        url: urls.productSearch(),
        body: search,
      },
      config: {
        enabled,
        placeholderData: keepPreviousData,
      },
    },
  );
};

export const useProductDealsSearchQuery = (
  search: WithStringValues<ProductDealsSearch>,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<ProductDeals, WithStringValues<ProductDealsSearch>>(
    keys.productDealsSearch(search),
    {
      fetch: { url: urls.productDealsSearch(), body: search },
      config: {
        enabled,
        placeholderData: keepPreviousData,
      },
    },
  );
};

export const useRebateItemsQuery = (id: number, search: RebateItemsSearch) => {
  return useFetchQuery<PromotionItems, WithStringValues<RebateItemsSearch>>(
    keys.rebateItemList(id, search),
    {
      fetch: { url: urls.rebateItems(id), body: search },
      config: {
        placeholderData: keepPreviousData,
      },
    },
  );
};

export const useIncentiveItemsQuery = (
  id: number,
  search: IncentiveItemsSearch,
  { enabled = !!id }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<PromotionItems, WithStringValues<IncentiveItemsSearch>>(
    keys.incentiveItemList(id, search),
    {
      fetch: { url: urls.incentiveItems(id), body: search },
      config: {
        enabled,
        placeholderData: keepPreviousData,
      },
    },
  );
};

export const useBrowseQuery = (
  search: WithStringValues<BrowseSearch> = {
    species: BrowseAllPatientKind.ALL,
  },
  { enabled }: { enabled: boolean },
) => {
  return useFetchQuery<IClientBrowse, WithStringValues<BrowseSearch>>(keys.browseFilter(search), {
    fetch: { url: urls.browse(), body: search },
    config: { placeholderData: keepPreviousData, enabled },
  });
};

export const useCarouselDisplayQuery = (
  search: WithStringValues<BrowseSearch> = {
    species: BrowseAllPatientKind.ALL,
  },
  { enabled }: { enabled: boolean },
) => {
  return useFetchQuery<CarouselDisplay, WithStringValues<BrowseSearch>>(
    keys.carouselsFilter(search),
    {
      fetch: { url: urls.carousels(), body: search },
      config: { placeholderData: keepPreviousData, enabled },
    },
  );
};

export const useCategoryQuery = (id: number) => {
  return useFetchQuery<ClientCategory>(keys.categoryDetail(id), {
    fetch: { url: urls.category(id) },
  });
};

export const useIncentiveQuery = (id: number = 0) => {
  return useFetchQuery<IncentiveDetails>(keys.incentiveDetail(id), {
    fetch: { url: urls.incentive(id) },
    config: { enabled: !!id },
  });
};

export const useRebateQuery = (id: number) => {
  return useFetchQuery<RebateDetails>(keys.rebateDetail(id), {
    fetch: { url: urls.rebate(id) },
  });
};

export const useProductLineAvailabilityQuery = (
  id: number,
  search: WithStringValues<ProductLineAvailabilitySearch>,
) => {
  return useFetchQuery<
    ProductLineAvailabilityResponse,
    WithStringValues<ProductLineAvailabilitySearch>
  >(keys.productLineDetailAvailability(id, search), {
    fetch: { url: urls.productLineAvailability(id), body: search },
  });
};

/**
 * Query to fetch accurate server-side pricing for add-to-cart button
 * Returns total price including discounts, fees, and shipping with MAP pricing enforcement
 */
export const useItemAddToCartPricing = (
  itemId: number,
  quantity: number,
  isSubscriptionPrice?: boolean,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<ItemPricingResponse, WithStringValues<ItemPricingSearch>>(
    keys.itemPrices(itemId, {
      quantity,
      is_subscription_price: isSubscriptionPrice ?? false,
    }),
    {
      fetch: {
        url: urls.itemAddToCartPricing(itemId),
        body: {
          quantity: quantity.toString(),
          is_subscription_price: isSubscriptionPrice?.toString() ?? "false",
        },
      },
      config: {
        enabled: enabled && !!itemId && quantity > 0,
      },
    },
  );
};

type FilterPanelOptions<TSearch, TFilterOptions> = {
  definitions: FilterDefinition<TSearch, FilterValue, TFilterOptions>[];
  response: Optional<{ filters: TFilterOptions }>;
  filters: TSearch;
  onChange: (next: Partial<TSearch>) => void;
  /** Set false by a surface that hides its filters even when the options would fill a panel. */
  isEnabled?: boolean;
};

/** The state `FilterPanel.Rail` and `FilterPanel.AppliedRow` render from. */
export const useFilterPanel = <TSearch, TFilterOptions>({
  definitions,
  response,
  filters,
  onChange,
  isEnabled = true,
}: FilterPanelOptions<TSearch, TFilterOptions>) => {
  const { isTabletLandscapeAndSmaller } = useBreakpoints();
  const filterOptions = response?.filters;

  const showFilterPanel =
    isEnabled &&
    !isTabletLandscapeAndSmaller &&
    !!filterOptions &&
    filtersWithOptions({ definitions, filterOptions }).length > 0;

  return {
    filterOptions,
    showFilterPanel,
    hasSelectedFilters: hasAnySelectedFilterValue(definitions, filters),
    clearAll: () => onChange(clearedFilterValues(definitions)),
    // Echoed back so the renderers below need only this one object.
    definitions,
    filters,
    onChange,
  };
};

export type FilterPanelState<TSearch, TFilterOptions> = ReturnType<
  typeof useFilterPanel<TSearch, TFilterOptions>
>;
